import React from "react";
import { motion } from "framer-motion";
import {
  FaUserCog,
  FaChartLine,
  FaBullhorn,
  FaLock,
  FaLightbulb,
  FaShieldAlt
} from "react-icons/fa";
import { IoMdRocket } from "react-icons/io";
import "./About.css";
import NavBar from "../../components/NavBar/NavBar";
import Footer from "../../components/Footer/Footer";
import { useLanguage } from "../../contexts/LanguageContext";
import FloatingWebsiteNavigation from "../../components/FloatingWebsiteNavigation/FloatingWebsiteNavigation";
import PressCTA from "../../components/PressCTA/PressCTA";

const About = () => {
  const { translations: t, language } = useLanguage();
  const isRTL = language === "ar" || language === "fa" || language === "ur";

  // Reusable variants
  const fadeUp = {
    hidden: { opacity: 0, y: 32 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } }
  };

  const fadeUpSlow = {
    hidden: { opacity: 0, y: 36 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.75, ease: "easeOut" } }
  };

  const containerStagger = {
    hidden: {},
    visible: {
      transition: {
        staggerChildren: 0.12,
        delayChildren: 0.05
      }
    }
  };

  const listStagger = {
    hidden: {},
    visible: {
      transition: { staggerChildren: 0.1, delayChildren: 0.1 }
    }
  };

  const fromLeft = {
    hidden: { opacity: 0, x: -40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const fromRight = {
    hidden: { opacity: 0, x: 40 },
    visible: { opacity: 1, x: 0, transition: { duration: 0.7, ease: "easeOut" } }
  };

  const floatTag = (delay = 0) => ({
    hidden: { opacity: 0, y: 10, scale: 0.98 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: { duration: 0.5, delay, ease: "easeOut" }
    }
  });

  // Key Features (2nd section)
  const keyFeatures = [
    {
      icon: <FaUserCog />,
      title: t.about?.keyFeatures?.customizableProfiles?.title || "Customizable Profiles",
      description:
        t.about?.keyFeatures?.customizableProfiles?.description ||
        "Tailor your profile for personal or commercial use with flexible layouts."
    },
    {
      icon: <FaChartLine />,
      title: t.about?.keyFeatures?.digitalExpansion?.title || "Digital Expansion",
      description:
        t.about?.keyFeatures?.digitalExpansion?.description ||
        "Powerful tools to grow your digital presence and reach new markets."
    },
    {
      icon: <FaBullhorn />,
      title: t.about?.keyFeatures?.contentPromotion?.title || "Content Promotion",
      description:
        t.about?.keyFeatures?.contentPromotion?.description ||
        "Easily publish and promote offers through our intuitive interface."
    },
    {
      icon: <FaLock />,
      title: t.about?.keyFeatures?.privacyCompliance?.title || "Privacy Compliance",
      description:
        t.about?.keyFeatures?.privacyCompliance?.description ||
        "Highest standards of digital security and data protection."
    }
  ];

  // Mission image
  const missionImageUrl =
    t.about?.mission?.imageUrl ||
    "https://iwinto.com/static/media/business10.7537e6fe7a471bf9efe9.jpg";

  // Usage Recommendations (4th section)
  const usageRecs = [
    {
      icon: <IoMdRocket />,
      title: t.about?.usage?.digitalExpansion?.title || "Digital Expansion Opportunities",
      items: [
        t.about?.usage?.digitalExpansion?.point1 || "Engage with digital markets effectively",
        t.about?.usage?.digitalExpansion?.point2 || "Promote content and services seamlessly"
      ]
    },
    {
      icon: <FaUserCog />,
      title: t.about?.usage?.profileSetup?.title || "Profile Setup",
      items: [
        t.about?.usage?.profileSetup?.point1 || "Create personal or commercial profiles",
        t.about?.usage?.profileSetup?.point2 || "Flexible customization options"
      ]
    },
    {
      icon: <FaShieldAlt />,
      title: t.about?.usage?.privacy?.title || "Privacy Compliance",
      items: [
        t.about?.usage?.privacy?.point1 || "Global data protection standards",
        t.about?.usage?.privacy?.point2 || "Secure digital environment"
      ]
    }
  ];

  return (
    <>
      <div className="nav-desktop-only">
        <NavBar />
      </div>
      <div className="about-page" dir={isRTL ? "rtl" : "ltr"}>
        {/* ---------- 1) HERO ---------- */}
        <section className="about-hero-section">
          <motion.div
            className="about-hero-inner"
            initial="hidden"
            animate="visible"
            variants={containerStagger}
          >
            <motion.h1 className="about-hero-title" variants={fadeUp}>
              {t.about?.hero?.titlePrefix || "iWinto is a strategy that cares for everyone."}
            </motion.h1>

            <motion.p className="about-hero-subtitle" variants={fadeUpSlow}>
              {t.about?.hero?.subtitle ||
                "A new interactive platform that offers multiple solutions to support the development of financial resources for individuals and companies."}
            </motion.p>

            <motion.div
              className="about-hero-stats"
              variants={containerStagger}
              initial="hidden"
              animate="visible"
            >
              <motion.div className="about-hero-stat" variants={fadeUp}>
                <div className="about-hero-stat-number">500K+</div>
                <div className="about-hero-stat-label">
                  {t.about?.stats?.targetUsers || "Target Users"}
                </div>
              </motion.div>
              <motion.div className="about-hero-stat" variants={fadeUp}>
                <div className="about-hero-stat-number">3M+</div>
                <div className="about-hero-stat-label">
                  {t.about?.stats?.projectedGrowth || "Projected Growth"}
                </div>
              </motion.div>
              <motion.div className="about-hero-stat" variants={fadeUp}>
                <div className="about-hero-stat-number">100%</div>
                <div className="about-hero-stat-label">
                  {t.about?.stats?.security || "Security Compliance"}
                </div>
              </motion.div>
            </motion.div>
          </motion.div>
        </section>

        {/* ---------- 2) KEY FEATURES ---------- */}
        <section className="about-key-features-section">
          <motion.div
            className="about-key-features-inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={containerStagger}
          >
            <motion.h2 className="about-key-title" variants={fadeUp}>
              {t.about?.keyFeatures?.title || "Key Features"}
            </motion.h2>

            <div className="about-key-grid">
              {keyFeatures.map((f, i) => (
                <motion.div
                  key={i}
                  className="about-key-card"
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                >
                  <motion.div
                    className="about-key-icon"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35, delay: 0.05 }}
                  >
                    <span className="about-key-icon-glyph">{f.icon}</span>
                  </motion.div>
                  <h3 className="about-key-card-title">{f.title}</h3>
                  <p className="about-key-card-text">{f.description}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ---------- 3) OUR MISSION ---------- */}
        <section className="about-our-mission-section">
          <div className="about-our-mission-inner">
            <motion.div
              className="mission-text"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fromLeft}
            >
              <h2 className="mission-title">
                {t.about?.mission?.title || "Our Mission"}
              </h2>
              <p className="mission-paragraph">
                {t.about?.mission?.text ||
                  "To revolutionize social commerce by providing a platform where users have full control over their digital presence and earnings potential."}
              </p>

              <h3 className="mission-values-title">
                {t.about?.values?.title || "Core Values"}
              </h3>

              <motion.ul
                className="mission-values-list"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, amount: 0.3 }}
                variants={listStagger}
              >
                {[
                  t.about?.values?.empowerment || "User Empowerment",
                  t.about?.values?.innovation || "Continuous Innovation",
                  t.about?.values?.security || "Uncompromising Security"
                ].map((val, i) => (
                  <motion.li key={i} variants={fadeUp}>
                    <span className="dot" />
                    {val}
                  </motion.li>
                ))}
              </motion.ul>
            </motion.div>

            <motion.div
              className="mission-visual"
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.2 }}
              variants={fromRight}
            >
              <img src={missionImageUrl} alt="Mission" loading="lazy" />
              <motion.div
                className="mission-tag tag-empower"
                variants={floatTag(0.05)}
                whileHover={{ scale: 1.04 }}
              >
                <IoMdRocket className="tag-icon" />
                <span>{t.about?.values?.empowerment || "Empowerment"}</span>
              </motion.div>
              <motion.div
                className="mission-tag tag-innovate"
                variants={floatTag(0.15)}
                whileHover={{ scale: 1.04 }}
              >
                <FaLightbulb className="tag-icon" />
                <span>{t.about?.values?.innovation || "Innovation"}</span>
              </motion.div>
              <motion.div
                className="mission-tag tag-security"
                variants={floatTag(0.25)}
                whileHover={{ scale: 1.04 }}
              >
                <FaLock className="tag-icon" />
                <span>{t.about?.values?.security || "Security"}</span>
              </motion.div>
            </motion.div>
          </div>
        </section>

        {/* ---------- 4) USAGE RECOMMENDATIONS ---------- */}
        <section className="about-usage-section">
          <motion.div
            className="about-usage-inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={containerStagger}
          >
            <motion.h2 className="about-usage-title" variants={fadeUp}>
              {t.about?.usage?.title || "Usage Recommendations"}
            </motion.h2>

            <div className="about-usage-grid">
              {usageRecs.map((card, idx) => (
                <motion.div
                  key={idx}
                  className="about-usage-card"
                  variants={fadeUp}
                  whileHover={{ y: -6 }}
                >
                  <motion.div
                    className="usage-icon"
                    initial={{ opacity: 0, scale: 0.9 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.35 }}
                  >
                    <span className="usage-icon-glyph">{card.icon}</span>
                  </motion.div>
                  <h3 className="about-usage-card-title">{card.title}</h3>
                  <motion.ul
                    className="about-usage-list"
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true, amount: 0.4 }}
                    variants={listStagger}
                  >
                    {card.items.map((it, i) => (
                      <motion.li key={i} variants={fadeUp}>
                        {it}
                      </motion.li>
                    ))}
                  </motion.ul>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>

        {/* ---------- 5) WHY CHOOSE IWinto (NEW LOOK) ---------- */}
        <section className="about-why-section">
          <motion.div
            className="about-why-inner"
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
            variants={containerStagger}
          >
            <motion.h2 className="about-why-title" variants={fadeUp}>
              {t.about?.why?.title || "Why Choose iWinto?"}
            </motion.h2>
            <motion.p className="about-why-subtitle" variants={fadeUpSlow}>
              {t.about?.why?.subtitle ||
                "iWinto stands out by offering a unique combination of social engagement and e-commerce capabilities, all within a secure and user-controlled environment."}
            </motion.p>

            <div className="about-why-points">
              {[
                t.about?.why?.p1 || "Seamless digital market integration",
                t.about?.why?.p2 || "Advanced promotional tools",
                t.about?.why?.p3 || "Compliance with global standards"
              ].map((txt, i) => (
                <motion.div key={i} className="about-why-point" variants={fadeUp}>
                  <span className="about-why-check">✓</span>
                  <span>{txt}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </section>
        <PressCTA/>
      </div>
      <Footer />
      <FloatingWebsiteNavigation />
    </>
  );
};

export default About;
